import { z } from "zod";
// What a country is called, from the one place that already knew: Customers has named countries since the port
// ([23](../../../docs/23-customers.md)), and two readings of "GB" would eventually disagree about one of them.
import { countryName } from "./customers.js";

/**
 * A business's own website, counted so the Studio knows what to make next
 * ([ADR 0035](../../../docs/adr/0035-a-visit-is-counted-and-nobody-is-followed.md), [03](../../../docs/03-studio-flow.md#analytics-tab-v1-minimal)).
 *
 * This is not a second Plausible and must not grow into one. Every number here has to end in a sentence somebody
 * could act on — *fifty-eight people read your lunch menu this week, make something about lunch* — which is why
 * there are pages, sources and enquiries and there are still no browsers, screen sizes, bounce rates, sessions or
 * funnels. A bakery cannot do anything with a bounce rate.
 *
 * Since 2026-09-22 it also reads the four things recorded at the door two days earlier and shown nowhere: where
 * they were, what they read it on, which tagged link brought them, and which website really did. Each is a count
 * of people that ends in the same kind of sentence — *most of them are on a phone, so make it vertical* — and none
 * of them is ever shown beside a person.
 *
 * It is also the only half of Analytics that can be built today: views and followers need a channel connection
 * nobody has, and a line of script on the customer's own site needs nothing but the customer.
 */

/** What a site key looks like, so a glance at one says what it is and a typo in the snippet is obvious. */
export const SITE_KEY_PREFIX = "jg_";

/** A view, or somebody reaching for the phone. The second is the one that matters and the first is how it is found. */
export const SiteEventKind = z.enum(["view", "enquiry"]);
export type SiteEventKind = z.infer<typeof SiteEventKind>;

/**
 * The four ways a local business is got hold of, and the only goals Joogoos knows by itself.
 *
 * DataFast and the rest ask you to define a conversion; a plumber will never do that. Three of these are found by
 * the script with nothing marked at all — a `tel:` link, a `mailto:`, a WhatsApp link — and `book` is the word a
 * site uses when it marks its booking button, so the answer to "did the post bring anyone?" needs no setting up.
 *
 * They are no longer the whole of it (2026-10-02, `SiteGoalName`): a site that marks something with a name of its
 * own is counted under that name. These four stay a list because they are the ones the product can say a sentence
 * about without being told what they mean.
 */
export const SiteGoal = z.enum(["call", "email", "whatsapp", "book"]);
export type SiteGoal = z.infer<typeof SiteGoal>;

/** What a goal the site names for itself has to look like: a short lower-case word, as an attribute would hold. */
export const SITE_GOAL_NAME = /^[a-z][a-z0-9_-]{0,31}$/;

/**
 * Any goal an enquiry can carry: one of the four, or a name the site itself gave (`data-joogoos-goal="quote"`).
 *
 * ADR 0035 §4 always said *anything the site marks*, and until 2026-10-02 the beacon took the four words above and
 * nothing else — so a form marked `invoice_upload` was answered 204 and written nowhere, and the owner was told
 * nobody had got in touch ([35](../../../docs/35-the-marketing-manager.md) reads that silence as a gap, and it was
 * ours). The name is the site's label for the thing and says nothing about the visitor — the script reads the
 * attribute and never a field — and anything that is not a short lower-case word is still dropped whole at the door.
 */
export const SiteGoalName = z.union([SiteGoal, z.string().regex(SITE_GOAL_NAME)]);
export type SiteGoalName = z.infer<typeof SiteGoalName>;

/**
 * The `utm_*` tags and nothing else from the query string.
 *
 * The query string is dropped whole at the door, because a booking form puts a name in one. These five keys are the
 * exception: they are a standard, they are written by whoever made the link rather than by the visitor, and without
 * them a customer who runs an ad can never be told whether it worked.
 */
export const SiteCampaign = z.object({
  source: z.string().max(64), medium: z.string().max(64), campaign: z.string().max(64),
  term: z.string().max(64), content: z.string().max(64),
}).partial();
export type SiteCampaign = z.infer<typeof SiteCampaign>;

/**
 * What the script sends, one object per event.
 *
 * Single letters because this crosses the wire on every page view of every customer's site, and because nothing
 * here is read by a person. There is no visitor id in it: the server makes one (ADR 0035) and the browser is never
 * told what it is, which is what stops the script becoming something that follows anybody.
 */
export const SiteBeacon = z.object({
  /** The site key from the snippet. An unknown one is answered exactly like a known one. */
  k: z.string().min(4).max(64),
  /** The path only — never the query string, which is where a booking form puts a name. */
  p: z.string().max(512),
  /** Where they came from, as the browser gives it. Kept only long enough to turn into a source. */
  r: z.string().max(512).optional(),
  /** `?j=` on a link Joogoos wrote, so a visit can be traced back to the post that sent it. */
  t: z.string().max(64).optional(),
  /**
   * Present on an enquiry, absent on a view: one of the four, or the name the site marked it with (`SiteGoalName`).
   * A malformed one fails the whole beacon, which the door answers with the same 204 as everything else.
   */
  g: SiteGoalName.optional(),
  /** The `utm_*` tags, and nothing else from the query string (`SiteCampaign`). */
  u: SiteCampaign.optional(),
});
export type SiteBeacon = z.infer<typeof SiteBeacon>;

/**
 * Who sent them, as a business would name it. Matched on the referring host, first match wins.
 *
 * Deliberately short: the point is to tell Instagram from Google from a friend's blog, not to maintain a directory
 * of the internet. Everything unmatched is "another website", which is a true and useful answer.
 */
const SOURCES: Array<[RegExp, string]> = [
  [/(^|\.)instagram\.com$/, "Instagram"],
  [/(^|\.)(facebook\.com|fb\.me)$/, "Facebook"],
  [/(^|\.)tiktok\.com$/, "TikTok"],
  [/(^|\.)(youtube\.com|youtu\.be)$/, "YouTube"],
  [/(^|\.)linkedin\.com$/, "LinkedIn"],
  [/(^|\.)(x\.com|twitter\.com|t\.co)$/, "X"],
  [/(^|\.)pinterest\.[a-z.]+$/, "Pinterest"],
  [/(^|\.)reddit\.com$/, "Reddit"],
  [/(^|\.)(whatsapp\.com|wa\.me)$/, "WhatsApp"],
  [/(^|\.)google\.[a-z.]+$/, "Google"],
  [/(^|\.)(bing\.com|duckduckgo\.com|search\.yahoo\.com|ecosia\.org)$/, "Search"],
  [/(^|\.)(mail\.google\.com|outlook\.[a-z.]+)$/, "Email"],
];

/**
 * The same names again, read from a `utm_source` instead of from a host (2026-09-21, the owner).
 *
 * It matters because the case a customer cares about most is the one the browser is worst at: a link in an Instagram
 * bio or an Instagram ad is opened by an in-app browser that sends **no referrer at all**, so the visit the customer
 * paid for was being counted as nobody having sent them. The tag on the link is the only thing left, and a customer
 * who tagged the link already said what it was.
 *
 * Short values as well as words, because people write `ig` and `fb` in a link they typed themselves. Anything
 * unmatched falls through to the customer's own word for it (`flyer`, `market-stall`), which is a better answer than
 * "another website" and is theirs rather than ours.
 */
const TAGGED: Array<[RegExp, string]> = [
  [/^(instagram|insta|ig)$/, "Instagram"],
  [/^(facebook|fb|meta)$/, "Facebook"],
  [/^tiktok$/, "TikTok"],
  [/^(youtube|yt)$/, "YouTube"],
  [/^linkedin$/, "LinkedIn"],
  [/^(x|twitter)$/, "X"],
  [/^pinterest$/, "Pinterest"],
  [/^reddit$/, "Reddit"],
  [/^whatsapp$/, "WhatsApp"],
  [/^(google|google-?ads|adwords)$/, "Google"],
  [/^(bing|duckduckgo|yahoo|ecosia)$/, "Search"],
  [/^(email|e-?mail|newsletter|mailchimp|klaviyo|brevo)$/, "Email"],
];

/**
 * The `utm_*` tags that were really there, as plain pairs. Nothing present is nothing kept, not an empty object.
 *
 * Here rather than beside the endpoint that writes them, because a sign-up keeps the same thing (`arrival.ts`) and two
 * readings of one standard would eventually disagree about something.
 */
export function campaignKept(campaign: SiteCampaign | null | undefined): Record<string, string> | null {
  const pairs = Object.entries(campaign ?? {}).filter((e): e is [string, string] => !!e[1]?.trim());
  return pairs.length ? Object.fromEntries(pairs.map(([k, v]) => [k, v.trim().slice(0, 64)])) : null;
}

/** What counts as the site's own domain, so `www.` is not a different website. */
export const sameSite = (host: string, domain: string): boolean => {
  const a = host.toLowerCase().replace(/^www\./, ""), b = domain.toLowerCase().replace(/^www\./, "");
  return a === b;
};

/** The host a referrer names, lowercased, or null when there is no referrer or it is not a URL. */
const hostOf = (referrer: string | null | undefined): string | null => {
  if (!referrer) return null;
  try { return new URL(referrer).hostname.toLowerCase() || null; } catch { return null; }
};

/**
 * What a `utm_source` is worth as a name, or null when there is no tag to read.
 *
 * One vocabulary with the hosts above, deliberately: a customer who tags some of their Instagram links and not
 * others must not end up with two rows called different things for the same place. A tag that names a host rather
 * than a place (`instagram.com`) is read by the host list, since somebody will write it that way.
 */
export function taggedSource(campaign: SiteCampaign | null | undefined): string | null {
  const tag = campaign?.source?.trim().toLowerCase();
  if (!tag) return null;
  for (const [pattern, name] of TAGGED) if (pattern.test(tag)) return name;
  for (const [pattern, name] of SOURCES) if (pattern.test(tag)) return name;
  const said = tag.replace(/[-_+.]+/g, " ").replace(/\s+/g, " ").trim().slice(0, 32);
  return said ? said[0]!.toUpperCase() + said.slice(1) : null;
}

/**
 * Who sent a visit, or `null` when nobody did.
 *
 * Null has two causes and they are the same answer: a page reached from another page of the same site is not a new
 * arrival, and nothing to read at all is somebody who typed the address. Counting either as a source would bury the
 * ones that are real.
 *
 * The browser is asked first, because a referrer is a fact and a tag is only what somebody wrote. The tag is read
 * when the browser said nothing — the Instagram case, where the referrer never arrives — and also when the browser
 * said only "some website", because a link shortener in front of a tagged link should not lose the name. A referrer
 * from the site's own pages stops the reading dead, tag or no tag: a `utm_` that sticks to the URL through an
 * internal click is not a second arrival, and counting it as one would double every tagged visit.
 */
export function sourceOf(referrer: string | null | undefined, domain: string | null, campaign?: SiteCampaign | null): string | null {
  const host = hostOf(referrer);
  if (host && domain && sameSite(host, domain)) return null;
  return sourceOfHost(host, campaign);
}

/**
 * The same reading, from a host that is already a host rather than a referrer.
 *
 * Split out because the other thing worth knowing where it came from is a **sign-up** (`arrival.ts`), and by then the
 * referrer is long gone and only the host was ever kept. One function, so "Instagram" cannot come to mean two
 * different things on two screens.
 */
export function sourceOfHost(host: string | null | undefined, campaign?: SiteCampaign | null): string | null {
  const h = host?.toLowerCase() || null;
  if (h) for (const [pattern, name] of SOURCES) if (pattern.test(h)) return name;
  return taggedSource(campaign) ?? (h ? "Another website" : null);
}

/**
 * What kind of thing somebody read it on, from the browser's own description of itself.
 *
 * Three values, and the reason is the product: a phone and a desk want different videos, and that is the only
 * decision this fact ever has to make. It is not a browser, not a version, not a screen size, and it cannot pick a
 * person out of a crowd — a million phones answer this identically.
 */
export function deviceOf(agent: string | null | undefined): SiteDeviceKind | null {
  if (!agent?.trim()) return null;
  if (/\b(iPad|Tablet)\b|Android(?!.*\bMobile\b)/i.test(agent)) return "tablet";
  if (/Mobi|iPhone|iPod|Android|Windows Phone/i.test(agent)) return "phone";
  return "desktop";
}

/** The three devices, as a business would say them. A "desktop" is a computer to everybody who is not us. */
export const DEVICE_WORDS: Record<SiteDeviceKind, string> = { phone: "Phone", tablet: "Tablet", desktop: "Computer" };

/** The three answers `deviceOf` can give. A kind of thing, never a model, a browser or a screen size. */
export type SiteDeviceKind = "phone" | "tablet" | "desktop";

/**
 * The host a visit was referred from, and never more than the host.
 *
 * A full referrer carries a path and a query — somebody's search terms, or worse — and none of that is ours to
 * keep. The host alone answers "who sent them" for ever, including for names we have not heard of yet: the source
 * on the row beside it is only today's reading of this, and a reading can be redone.
 */
export function referrerHost(referrer: string | null | undefined, domain: string | null): string | null {
  if (!referrer) return null;
  let host: string;
  try { host = new URL(referrer).hostname.toLowerCase().replace(/^www\./, ""); } catch { return null; }
  if (!host || (domain && sameSite(host, domain))) return null;
  return host.slice(0, 128);
}

/** A path as a person would say it: `/menus/lunch-menu` is "lunch menu", and `/` is the home page. */
export function pageName(path: string): string {
  const last = path.replace(/[?#].*$/, "").replace(/\.(html?|php|aspx?)$/i, "").split("/").filter(Boolean).pop();
  if (!last) return "your home page";
  return decodeURIComponent(last).replace(/[-_+]+/g, " ").trim().toLowerCase();
}

export interface SitePage { path: string; visitors: number; enquiries: number }
export interface SiteSource { name: string; visitors: number; enquiries: number }
/** A post that sent people, by its `?j=` tag. Empty until publishing writes those links. */
export interface SitePost { tag: string; visitors: number; enquiries: number }
/** Letters to one kind of customer that sent people, by the group's `?j=` tag (`letterTag`), named as the group is. */
export interface SiteLetters { group: string; visitors: number; enquiries: number }

/**
 * The four things recorded at the door since 2026-09-20 and read by nothing until now
 * (ADR 0035, second amendment 2026-09-22: *"a screen can be built at any time from data already recorded"* — this
 * is that screen).
 *
 * They are lists of **people**, counted the same way everything else here is counted, and they are lists rather
 * than facts beside a row on purpose: "France, 4 people" is a place a business can think about, and "this visitor
 * was in France on a phone" is the beginning of following somebody, which this product does not do.
 */
export interface SiteCountry { code: string; visitors: number; enquiries: number }
export interface SiteDevice { device: SiteDeviceKind; visitors: number; enquiries: number }
/**
 * A `utm_campaign` that sent people — the *name on the link*, and only that.
 *
 * Not the `utm_source`, which since 2026-09-21 is read as the source itself (`taggedSource`): a link tagged
 * `utm_source=newsletter` is already a row called Email in the list above, and saying it twice in two lists would
 * make one visit look like two. `SiteCampaign` is the tags themselves; this is a row of the screen.
 */
export interface SiteCampaignRow { name: string; visitors: number; enquiries: number }
/** The host itself, behind the reading `source` made of it: `sourdough.blog`, not "Another website". */
export interface SiteReferrer { host: string; visitors: number; enquiries: number }

/**
 * Who the panel is looking at, and the reason the whole thing is worth building twice.
 *
 * A filter is a **set of people**, never a set of rows: picking Instagram means everybody whose visit started
 * there, including the pages they read afterwards, and picking a page means everybody who saw it, including where
 * they came from and what else they looked at. Filtering rows instead would answer "which events say Instagram",
 * which is a question nobody has, and would quietly hide every second page view (they carry no source at all).
 *
 * The point of it is the ideas underneath: *what should I make for Instagram* is a different question from *what
 * should I make*, and it is the one a person posting to Instagram on Friday actually has.
 */
export interface SiteFilter {
  source?: string | null;
  path?: string | null;
  /** Two letters, as the edge gave them. */
  country?: string | null;
  device?: string | null;
}

/** Every part of a filter, in the order the chips and the sentence say them. One list so they can never disagree. */
export const FILTER_KEYS = ["source", "path", "country", "device"] as const;

/** Whether anything is being looked through. */
export const filtered = (f: SiteFilter | undefined): boolean => FILTER_KEYS.some((k) => !!f?.[k]);

/**
 * What a filter says about the people it names, as the clause that follows "the people who".
 *
 * One reading, used by the chips at the top of the screen and by the headline underneath them, because a chip
 * saying one thing above a sentence saying another is the screen quietly lying about what it counted.
 */
export function filterWords(f: SiteFilter | undefined): Array<{ key: (typeof FILTER_KEYS)[number]; label: string }> {
  const out: Array<{ key: (typeof FILTER_KEYS)[number]; label: string }> = [];
  if (f?.source) out.push({ key: "source", label: `came from ${f.source}` });
  if (f?.path) out.push({ key: "path", label: `saw ${pageName(f.path)}` });
  if (f?.country) out.push({ key: "country", label: `were in ${countryName(f.country)}` });
  if (f?.device) out.push({ key: "device", label: `were on a ${(DEVICE_WORDS[f.device as SiteDeviceKind] ?? f.device).toLowerCase()}` });
  return out;
}

/** The window, counted. Everything the tab draws and everything an idea is made of. */
export interface SiteSummary {
  /** People, counted once a day each (ADR 0035), not page views. */
  visitors: number;
  views: number;
  enquiries: number;
  /** Visits carrying a `?j=` tag: the ones a Joogoos post sent. Zero until something is published. */
  fromPosts: number;
  /** Visits a letter's link sent, by the group the letter was written for (2026-10-21). Never a person's: a group's. */
  fromLetters: number;
  /**
   * People on the website in the last five minutes — and never filtered, because "now" is not a window.
   *
   * It earns its place: a story goes out and this moves, which is the post-to-traffic link felt rather than read
   * about a day later in a table.
   */
  live: number;
  /** One point a day, oldest first, to match the post chart above it. `enquiries` marks the days somebody rang. */
  series: Array<{ date: string; visitors: number; enquiries: number }>;
  sources: SiteSource[];
  pages: SitePage[];
  posts: SitePost[];
  letters: SiteLetters[];
  /** Where they were, what they read it on, which tagged link brought them, and which website really did. */
  countries: SiteCountry[];
  devices: SiteDevice[];
  campaigns: SiteCampaignRow[];
  referrers: SiteReferrer[];
  /** The same window ending where this one begins, for "more than last month". Null when there is nothing to compare. */
  before: { visitors: number; enquiries: number } | null;
}

/** People who got in touch, as a share of people who came. The one rate worth showing, and only above a handful. */
export const touchRate = (s: { visitors: number; enquiries: number }): string | null =>
  s.visitors >= 10 ? `${((s.enquiries / s.visitors) * 100).toFixed(s.enquiries / s.visitors >= 0.1 ? 0 : 1)}% got in touch` : null;

/**
 * One thing to make, with the reason it is being suggested.
 *
 * `text` is the sentence on the card and `idea` is what is written into the brief when the button is pressed — the
 * same thing said twice, once to a person and once to the Studio, because a sentence that reads well on a card
 * ("58 people read your lunch menu") is a poor brief.
 */
export interface SiteIdea { text: string; idea: string }

/** Below this, a page is noise rather than a signal, and a suggestion made from it would be embarrassing. */
const ENOUGH = 3;

/**
 * And a verdict about the whole website needs more than a handful (2026-09-20).
 *
 * "Most people only saw your home page" is a judgement on the site, not an observation about a page, and said over
 * three visitors it is presumptuous — on a website counted since yesterday it would be the first sentence the
 * customer ever read from us.
 */
const ENOUGH_FOR_A_VERDICT = 10;

/**
 * What to make next, from what the website did. Rules, not a model: it costs nothing, it is the same every time, and
 * every sentence can be traced to a number on the same screen. A model over the top of this is a later question
 * ([14](../../../docs/14-open-items.md)).
 *
 * At most three, strongest first, and never two about the same page.
 */
export function ideasFrom(s: SiteSummary, filter: SiteFilter = {}): SiteIdea[] {
  const out: SiteIdea[] = [];
  const used = new Set<string>();
  // The sentence is capitalised where it starts, which is the prefix when there is one and the fact when there is
  // not — a card reading "someone got in touch" was the first thing the filter broke.
  const add = (path: string, text: string, idea: string) => {
    if (used.has(path) || out.length >= 3) return;
    used.add(path);
    out.push({ text: cap(text), idea });
  };
  // Under a filter every sentence is about those people and every brief is for that place. "22 people looked at
  // lunch menu" and "22 of the people Instagram sent looked at lunch menu" are different facts, and a card that
  // said the first while showing the second would be the screen lying quietly.
  const of = filter.source ? `of the people ${filter.source} sent, ` : "";
  const For = filter.source ? `, for ${filter.source}` : "";

  // Somebody reached for the phone on a page. Nothing else on this screen is worth as much.
  const asked = [...s.pages].filter((p) => p.enquiries > 0).sort((a, b) => b.enquiries - a.enquiries)[0];
  if (asked) {
    add(asked.path, `${of}${asked.enquiries === 1 ? "someone" : `${asked.enquiries} people`} got in touch from ${pageName(asked.path)}.`,
      `A short video about ${pageName(asked.path)}, ending on how to get in touch${For}.`);
  }

  // The busiest page that is not the front door: what people came looking for.
  const read = s.pages.filter((p) => p.path !== "/" && p.visitors >= ENOUGH).sort((a, b) => b.visitors - a.visitors)[0];
  if (read) {
    add(read.path, `${of}${read.visitors} people looked at ${pageName(read.path)}.`, `A short video about ${pageName(read.path)}${For}.`);
  }

  // A busy site whose visitors all stop at the front door is a business whose website is not saying what it sells.
  if (!read && s.visitors >= ENOUGH_FOR_A_VERDICT) {
    add("/", `${of}${s.visitors} people came and most only saw your home page.`, `A short video introducing what we sell and who we are${For}.`);
  }

  // Where they are coming from, as a place to put the next one. Last, because it is the weakest of the three —
  // and not said at all when a source is already being looked through, where it would only repeat the chip.
  const from = filter.source ? undefined : s.sources[0];
  if (from && from.visitors >= ENOUGH && from.name !== "Another website") {
    add(`source:${from.name}`, `${from.visitors} of them came from ${from.name}.`, `Another post for ${from.name}, about what we do best.`);
  }
  return out;
}

const cap = (s: string) => (s ? s[0]!.toUpperCase() + s.slice(1) : s);

/** The line the customer pastes into their site. One line, cookieless, and nothing to configure. */
export function siteSnippet(key: string, origin: string): string {
  return `<script defer data-joogoos="${key}" src="${origin.replace(/\/$/, "")}/j.js"></script>`;
}

/** `GET /v1/site`, and what `POST /v1/site` answers with once a domain is given. */
export interface SiteResponse {
  /** A key has been made and at least one event has arrived: the script is in, and it works. */
  connected: boolean;
  /** The website being counted, without the scheme. Null until the owner says which it is. */
  domain: string | null;
  /**
   * The website the Business page already knows, as a bare host, when this workspace has not been told which site to
   * count. Joogoos read it weeks ago; asking a bakery to type it again is asking for something we have.
   */
  known: string | null;
  /** Null until a domain is given; there is nothing to paste before then. */
  key: string | null;
  snippet: string | null;
  /** When the first event arrived, which is the only proof the snippet is really in the page. */
  installedAt: string | null;
  days: number;
  /** Which people were counted, echoed back so a reloaded tab draws the chips it is actually showing. */
  filter: Required<SiteFilter>;
  summary: SiteSummary | null;
  ideas: SiteIdea[];
}

/** `POST /v1/site`: the website to count. A bare host, however the owner types it. */
export const SiteConnect = z.object({ domain: z.string().min(3).max(253) });

/**
 * A domain as the owner typed it, reduced to a host: a scheme, a path and a port are all things somebody will
 * paste and none of them make it a different website. Null when what was typed is not a host at all.
 *
 * **`www.` is kept**, which it was not until 2026-09-21. It made no difference while this was only the domain
 * visits are counted for — `sameSite` treats `www.` and the bare host as one site, and always did. It makes a
 * difference now that this is the business's one address and articles are published under it
 * ([ADR 0040](../../../docs/adr/0040-a-business-has-one-website-address-and-it-is-typed-on-the-business-page.md)):
 * a site whose canonical host is `www.` would have had every article's address, canonical tag and JSON-LD written
 * to the other one. What somebody types is what their website is.
 *
 * The last label has to be letters, because a word is not a website: `https://https` was stored and served by the
 * dev box on 2026-09-21 through the second address field this rule now replaces, and everything the product built
 * — the feed, each article's address, the link Google is asked about — was built on it.
 */
export function cleanDomain(typed: string): string | null {
  const host = typed.trim().toLowerCase()
    .replace(/^[a-z]+:\/\//, "")
    .replace(/[/?#].*$/, "")
    .replace(/:\d+$/, "");
  return /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*\.[a-z]{2,}$/.test(host) ? host : null;
}

/**
 * The `?j=` a letter's links carry: one per group, never per letter (2026-10-21).
 *
 * Joogoos does not see the answers to a send-only mailbox's letters, so the link in the ask is how it learns whether
 * a kind of letter worked: *the Dubai letters sent 3 people, and 1 booked*. It names the group, not the company or
 * the person, because a tag per letter would follow a named reader to a website, which is the one thing ADR 0035 is
 * named against. Letters only, `l` and eight of them, so the guard never reads a number in it and a person reading
 * the link sees nothing that describes them.
 */
export function letterTag(groupId: string): string {
  const hex = groupId.replace(/[^0-9a-f]/gi, "").toLowerCase().slice(-8).padStart(8, "0");
  return `l${[...hex].map((c) => String.fromCharCode(97 + parseInt(c, 16))).join("")}`;
}

export const isLetterTag = (tag: string): boolean => /^l[a-p]{8}$/.test(tag);

/**
 * A letter's words with `?j=` added to every link on the business's own website, and to nothing else.
 *
 * Only its own site, because that is where the Joogoos line is and so the only place the tag is ever read; a link
 * to anybody else's site is left exactly as the owner typed it. A link that already carries a query keeps it.
 */
export function tagLinks(text: string, website: string | null | undefined, tag: string): string {
  const home = (website ?? "").trim().replace(/^[a-z][a-z0-9+.-]*:\/\//i, "").replace(/^www\./i, "").split(/[/?#]/)[0]!.toLowerCase();
  if (!home || !tag) return text;
  return text.replace(/(?<![@\w.-])((?:https?:\/\/)?(?:www\.)?)([a-z0-9][a-z0-9-]*(?:\.[a-z0-9-]+)*\.[a-z]{2,})(\/[^\s?#]*)?(\?[^\s#]*)?(#\S*)?(?![@\w-])/gi,
    (whole, scheme: string, host: string, path: string | undefined, query: string | undefined, hash: string | undefined) => {
      const h = host.toLowerCase();
      if (h !== home && !h.endsWith(`.${home}`)) return whole;
      // A sentence's last full stop or comma is the sentence's, not the link's.
      const tail = /[.,;:!)]+$/.exec(path ?? "")?.[0] ?? "";
      const bare = tail ? (path ?? "").slice(0, -tail.length) : (path ?? "");
      if (query && /[?&]j=/.test(query)) return whole;
      const q = query ? `${query}&j=${tag}` : `?j=${tag}`;
      return `${scheme}${host}${bare}${q}${hash ?? ""}${tail}`;
    });
}

